/**
 * Shared, side-effect-free logic for the partner map view.
 *
 * The gid-api endpoint returns a *matrix* — per partner group, per job status: jobs,
 * revenue, cost, revenue incl. products, service hours. Nothing about it presumes which
 * statuses "count". That decision lives here, in `recompute()`, so the view can re-filter
 * without another round trip.
 *
 * The one rule that must not be blurred: only `completed` and `invoice sent` are billed.
 * Everything else is backlog and is reported separately as "… in Arbeit" / "… in progress".
 * Prices are attached at order creation, so a cancelled job carries a revenue figure too —
 * summing it into "revenue" would overstate the year by a wide margin.
 */

/** [i18n key suffix, group] per job status. Group drives presets and the chip colour. */
export const STATUS_META = {
  completed: ['completed', 'billed'],
  'invoice sent': ['invoice-sent', 'billed'],
  'approval pending': ['approval-pending', 'open'],
  new: ['new', 'open'],
  accepted: ['accepted', 'open'],
  'appointment confirmed': ['appointment-confirmed', 'open'],
  started: ['started', 'open'],
  finished: ['finished', 'open'],
  'waiting prepayment': ['waiting-prepayment', 'open'],
  'on hold': ['on-hold', 'open'],
  'on hold before accepted': ['on-hold-before-accepted', 'open'],
  'on hold after protocol approval': ['on-hold-after-protocol', 'open'],
  'proposal approval pending': ['proposal-approval-pending', 'quote'],
  'brand proposal approval pending': [
    'brand-proposal-approval-pending',
    'quote',
  ],
  cancelled: ['cancelled', 'ended'],
  closed: ['closed', 'ended'],
};

export const GROUP_ORDER = ['billed', 'open', 'quote', 'ended'];

export const GROUP_COLOR = {
  billed: 'var(--pm-margin)',
  open: 'var(--pm-partner)',
  quote: 'var(--pm-warn)',
  ended: 'var(--pm-crit)',
};

export const statusGroup = (key) => (STATUS_META[key] || ['', 'open'])[1];
export const isBilled = (key) => statusGroup(key) === 'billed';

/** Every status actually present in the data, ordered by group then job count. */
export function collectStatuses(partners) {
  const jobs = {};
  partners.forEach((p) => {
    Object.keys(p.by_status || {}).forEach((k) => {
      jobs[k] = (jobs[k] || 0) + p.by_status[k][0];
    });
  });
  return Object.keys(jobs)
    .sort((a, b) => {
      const ga = GROUP_ORDER.indexOf(statusGroup(a));
      const gb = GROUP_ORDER.indexOf(statusGroup(b));
      return ga !== gb ? ga - gb : jobs[b] - jobs[a];
    })
    .map((k) => ({ key: k, group: statusGroup(k), jobs: jobs[k] }));
}

/** Default selection = the billed statuses, i.e. the admin partner list's definition. */
export function defaultStatuses(allStatuses) {
  return allStatuses.filter((s) => s.group === 'billed').map((s) => s.key);
}

const r2 = (v) => Math.round(v * 100) / 100;

/**
 * Derive each partner group's headline figures from the selected statuses.
 * Returns a new array — the raw API rows are never mutated.
 */
export function recompute(partners, selected) {
  const keys = selected instanceof Set ? selected : new Set(selected);
  return partners.map((p) => {
    let jobs = 0;
    let rev = 0;
    let cost = 0;
    let revProducts = 0;
    let hours = 0;
    let jobsWip = 0;
    let revWip = 0;
    let costWip = 0;
    keys.forEach((k) => {
      const v = (p.by_status || {})[k];
      if (!v) return;
      if (isBilled(k)) {
        jobs += v[0];
        rev += v[1];
        cost += v[2];
        revProducts += v[3];
        hours += v[4];
      } else {
        jobsWip += v[0];
        revWip += v[1];
        costWip += v[2];
      }
    });
    return {
      ...p,
      jobs_year: jobs,
      revenue_year: r2(rev),
      cost_year: r2(cost),
      margin_year: r2(rev - cost),
      margin_pct_year: rev ? Math.round(((rev - cost) / rev) * 1000) / 10 : 0,
      revenue_incl_products_year: r2(revProducts),
      service_hours_year: Math.round(hours * 10) / 10,
      jobs_wip: jobsWip,
      revenue_wip: r2(revWip),
      cost_wip: r2(costWip),
      margin_wip: r2(revWip - costWip),
      jobs_all: jobs + jobsWip,
      // Ranking and dot size only — never presented as "revenue".
      revenue_all: r2(rev + revWip),
    };
  });
}

/* ---------- geo ---------- */

const RAD = Math.PI / 180;

/** Great-circle distance in km. */
export function distanceKm(aLat, aLon, bLat, bLon) {
  const dLat = (bLat - aLat) * RAD;
  const dLon = (bLon - aLon) * RAD;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * RAD) * Math.cos(bLat * RAD) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Nearest entry of `candidates` to `origin`, or null. Both need lat/lon. */
export function nearest(origin, candidates) {
  if (!origin || origin.lat == null) return null;
  let best = null;
  candidates.forEach((c) => {
    if (c === origin || c.lat == null) return;
    const km = distanceKm(origin.lat, origin.lon, c.lat, c.lon);
    if (!best || km < best.km) best = { ref: c, km: Math.round(km) };
  });
  return best;
}

/* ---------- hero utilization gauge ---------- */

/**
 * A Mobile Hero is drawn as a gauge: the outer diamond is identical for everyone and
 * means 100 % net capacity; the filled inner diamond is the measured utilization, linear
 * in the value. Over 100 % it spills past the frame and turns critical-red.
 */
export const HERO_R_100 = 11;

export const heroDiamond = (x, y, r) =>
  `M${x} ${y - r}L${x + r} ${y}L${x} ${y + r}L${x - r} ${y}Z`;

export const heroFillR = (u) => HERO_R_100 * Math.max(0.06, Math.min(1.35, u));

/** Capacity-weighted mean over the heroes that have Dataverse data. */
export function heroUtilAvg(heroes) {
  let booked = 0;
  let capacity = 0;
  heroes.forEach((h) => {
    if (h.util) {
      booked += h.util.booked_h;
      capacity += h.util.net_cap_h;
    }
  });
  return capacity ? booked / capacity : null;
}

/* ---------- formatting ---------- */

export function formatters(locale) {
  const eur = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
  const num = new Intl.NumberFormat(locale);
  const dec = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return {
    money: (v) => (v == null ? '–' : eur.format(v)),
    num: (v) => (v == null ? '–' : num.format(v)),
    dec: (v) => (v == null ? '–' : dec.format(v)),
    /** `v` is a fraction (0.736 → "73,6 %"). */
    pctOfOne: (v) =>
      v == null ? '–' : `${num.format(Math.round(v * 1000) / 10)} %`,
    /** `v` is already a percentage (73.6 → "73,6 %"). */
    pct: (v) => (v == null ? '–' : `${num.format(Math.round(v * 10) / 10)} %`),
  };
}

/** Trim legal-form noise so a name fits a bar row or a map label. */
export function shortName(name) {
  return String(name || '')
    .replace(/\s*(GmbH|OHG|GbR|e\.\s?K\.|Inh\..*|&\s?Co\..*)/gi, '')
    .trim()
    .slice(0, 26);
}
